import { mergeMap, catchError } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import get from 'lodash.get';
import { EMPTY, of } from 'rxjs';
import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { DocumentService, isPrintSettingSetToDownload } from '../../services/document-service';
import { DocumentType } from '../../enums/document-type';
import { getPrintSettingAndCheckIsOfflinePrinter } from '../shipstation-connect/ss-connect.helpers';

import * as Actions from './print.actions';
import * as DocumentActions from '../documents/documents.actions';
import * as Api from './print.api';
import { messages } from './messages';

export const generateOrderSummary = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.GENERATE_ORDER_SUMMARY),
    mergeMap(({ fulfillmentPlanIds, shouldOpenPrintModal }) => {
      const state = getState();

      const { printSetting, isOfflinePrinter } = getPrintSettingAndCheckIsOfflinePrinter({
        state,
        printSettingKey: DocumentType.OrderSummary.printSettingKey,
      });

      if (isOfflinePrinter) {
        return EMPTY;
      }

      return Api.generateOrderSummaryDocument(fulfillmentPlanIds).pipe(
        mergeMap((resp) => {
          const url = get(resp, 'data.value');

          // download the document
          if (isPrintSettingSetToDownload(printSetting) && !shouldOpenPrintModal) {
            const downloadUrl = url.includes('?') ? `${url}&download=1` : `${url}?download=1`;
            DocumentService.downloadFile(downloadUrl);
            return of(Actions.documentDownloadStarted('order-summary', 1));
          }

          // print the document
          if (printSetting && !printSetting.disabled && !shouldOpenPrintModal) {
            return of(
              DocumentActions.printDocument(
                DocumentType.OrderSummary,
                fulfillmentPlanIds,
                DocumentService.getDefaultPrintSettingForDocumentType(state, DocumentType.OrderSummary, false),
                undefined,
                url,
              ),
            );
          }

          // open the print modal
          return of(Actions.openPrintModal(url, DocumentType.OrderSummary.identifier, fulfillmentPlanIds));
        }),
        catchError(() => {
          const orderSummaryErrorProps = {
            title: <FormattedMessage {...messages.errorCreatingOrderSummary} />,
            content: <FormattedMessage {...messages.unknownError} />,
            notificationType: NOTIFICATION_TYPE_ERROR,
          };
          NotificationService.showNotification(orderSummaryErrorProps);
          return of(Actions.closingPrintModal());
        }),
      );
    }),
  );
};

export const printOrderSummary = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_ORDER_SUMMARY),
    mergeMap((action) => {
      const { fulfillmentPlanIds } = action;
      const { shouldOpenPrintModal } = action;
      if ((fulfillmentPlanIds ?? []).length === 0) {
        return EMPTY;
      }
      return of(Actions.generateOrderSummary(fulfillmentPlanIds, shouldOpenPrintModal));
    }),
  );
};

export const printOrderSummaryFromShipmentsGrid = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_ORDER_SUMMARY_FROM_SHIPMENTS_GRID),
    mergeMap((action) => {
      const fulfillmentPlanIds = action.fulfillmentPlanIds || [];
      const { shouldOpenPrintModal } = action;
      return of(Actions.printOrderSummary(fulfillmentPlanIds, shouldOpenPrintModal));
    }),
  );
};
